import {Calendar} from '@fullcalendar/core';
import multiMonthPlugin from '@fullcalendar/multimonth';
import dayGridPlugin from '@fullcalendar/daygrid';
import iCalendarPlugin from '@fullcalendar/icalendar';
import interactionPlugin from '@fullcalendar/interaction';
import {env_is_local, env_service_url, ui_is_small_screen, ui_scroll_to, ui_shake} from './util.js';

const parallaxTargets = [];

document.querySelectorAll('[data-background]').forEach((element) => {
    let overlay = document.createElement('div');
    overlay.classList.add('overlay');
    overlay.style.backgroundImage = `url(${element.dataset.background})`;

    element.appendChild(overlay);
    element.classList.add('has-background');

    parallaxTargets.push({ el: element, target: overlay, factor: 0.55 });
});

const giteIntro = document.querySelector('.gite .intro');
if (giteIntro) {
    parallaxTargets.push({ el: giteIntro, factor: 0.55 });
}

let parallaxScheduled = false;
const updateParallax = () => {
    parallaxScheduled = false;
    const viewportH = window.innerHeight;
    for (const { el, target, factor } of parallaxTargets) {
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const offset = (center - viewportH / 2) * factor;
        (target ?? el).style.setProperty('--parallax-y', `${-offset}px`);
    }
};
const scheduleParallax = () => {
    if (parallaxScheduled) return;
    parallaxScheduled = true;
    requestAnimationFrame(updateParallax);
};
if (parallaxTargets.length) {
    updateParallax();
    window.addEventListener('scroll', scheduleParallax, { passive: true });
    window.addEventListener('resize', scheduleParallax);
}

const bookLink = document.querySelector('.gite .intro .book-link');
if (bookLink) {
    document.body.appendChild(bookLink);
}

const moreDetailsLink = document.querySelector('.gite .intro .more-details');
const detailsSectionForMore = document.querySelector('.gite > .details');
if (moreDetailsLink && detailsSectionForMore) {
    const updateMoreDetails = () => {
        const viewportH = window.innerHeight;
        const detailsTop = detailsSectionForMore.getBoundingClientRect().top;
        moreDetailsLink.classList.toggle('is-hidden', detailsTop <= viewportH / 2);
    };
    updateMoreDetails();
    globalThis.addEventListener('scroll', updateMoreDetails, { passive: true });
    globalThis.addEventListener('resize', updateMoreDetails);
}
const giteDetailsSection = document.querySelector('.gite > .details');
const giteBookSection = document.querySelector('.gite > .book');
if (bookLink && giteDetailsSection && giteBookSection) {
    const header = document.querySelector('header');
    const isLandscape = () => globalThis.matchMedia('(orientation: landscape)').matches;
    const updateBookLink = () => {
        const viewportH = window.innerHeight;
        const headerH = header?.offsetHeight ?? 0;
        const detailsTop = giteDetailsSection.getBoundingClientRect().top;
        const bookTop = giteBookSection.getBoundingClientRect().top;
        if (isLandscape()) {
            const detailsVisible = viewportH - detailsTop > 80;
            const hide = bookTop <= headerH + 80;
            bookLink.classList.toggle('is-floating', detailsVisible && !hide);
            bookLink.classList.toggle('is-hidden', hide);
        } else {
            bookLink.classList.remove('is-floating');
            bookLink.classList.toggle('is-hidden', bookTop <= viewportH - 80);
        }
    };
    updateBookLink();
    globalThis.addEventListener('scroll', updateBookLink, { passive: true });
    globalThis.addEventListener('resize', updateBookLink);
    globalThis.matchMedia('(orientation: landscape)').addEventListener('change', updateBookLink);

    /* Snap-on-rest: when the user stops scrolling within 15% of the viewport
     * around the booking section's top or bottom edge, snap it flush with the
     * header / viewport bottom. */
    let snapTimer = null;
    let isSnapping = false;
    const snapBookSection = () => {
        if (isSnapping) return;
        const viewportH = window.innerHeight;
        const headerH = header?.offsetHeight ?? 0;
        const rect = giteBookSection.getBoundingClientRect();
        const thresholdTop = viewportH * 0.25;
        const thresholdBottom = viewportH * 0.05;
        const distanceTop = rect.top - headerH;
        const distanceBottom = viewportH - rect.bottom;

        let scrollDelta = null;
        if (Math.abs(distanceTop) > 0 && Math.abs(distanceTop) < thresholdTop) {
            scrollDelta = distanceTop;
        } else if (Math.abs(distanceBottom) > 0 && Math.abs(distanceBottom) < thresholdBottom) {
            scrollDelta = -distanceBottom;
        }

        if (scrollDelta === null) return;

        isSnapping = true;
        window.scrollBy({ top: scrollDelta, behavior: 'smooth' });
        setTimeout(() => { isSnapping = false; }, 700);
    };
    const scheduleSnap = () => {
        if (snapTimer !== null) clearTimeout(snapTimer);
        snapTimer = setTimeout(() => {
            snapTimer = null;
            snapBookSection();
        }, 140);
    };
    globalThis.addEventListener('scroll', scheduleSnap, { passive: true });
}

/* Cache Gotan : relaie les appels sortants (agenda iCal, géocodage inverse) */
const defaultProxyCacheBase = () => env_is_local()
    ? __PROXY_CACHE_BASE_URL_LOCAL__
    : __PROXY_CACHE_BASE_URL_SERVER__;

const resolveBaseNights = (price) => Math.max(1, Number(price?.minNights) || 2);

const computeStayPrice = (price, nights) => {
    if (!Number.isFinite(nights) || nights <= 0) return 0;
    const base = Number(price?.base) || 0;
    if (base <= 0) return 0;
    const baseNights = resolveBaseNights(price);
    const fullPriceNights = Math.min(nights, baseNights);
    const extraNights = Math.max(0, nights - baseNights);
    const ratio = Number(price?.extraRatio) || 0;
    const roundedBaseNight = Math.round(base);
    const roundedExtraNight = Math.round(roundedBaseNight * ratio);
    return (roundedBaseNight * fullPriceNights) + (roundedExtraNight * extraNights);
};


document.addEventListener('DOMContentLoaded', function () {
    let calendarInstance = null;
    let bookedRanges = [];
    let overlapMsg = 'Ces dates chevauchent une réservation existante.';
    const CHECKIN_HOUR = 16;
    const CHECKOUT_HOUR = 10;
    /* Les heures d'arrivée et de départ sont celles du gîte, pas celles du visiteur :
     * sans fuseau explicite, Google Calendar les lirait dans celui de l'agenda. */
    const CALENDAR_TIMEZONE = 'Europe/Paris';

    // Use local (browser) date methods: FullCalendar without a timezone adapter passes
    // real local-midnight Dates to callbacks (not fake-UTC), so getDate() gives the correct cell date.
    function localDateStr(d) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    function dateHourMs(dateStr, hour) {
        const d = new Date(dateStr + 'T12:00:00');
        d.setHours(hour, 0, 0, 0);
        return d.getTime();
    }
    // Arithmetic on "YYYY-MM-DD" strings using UTC to avoid DST/timezone shifts.
    function addOneDay(dateStr) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const next = new Date(Date.UTC(y, m - 1, d + 1));
        return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`;
    }
    function subtractOneDay(dateStr) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const prev = new Date(Date.UTC(y, m - 1, d - 1));
        return `${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}-${String(prev.getUTCDate()).padStart(2, '0')}`;
    }
    document.querySelectorAll('.action').forEach(a => {
        a.addEventListener('click', function (event) {
            const target = document.querySelector(event.target.href.substring(event.target.href.indexOf('#')));
            if (target !== null) {
                event.preventDefault();
                let offset = target.offsetTop;
                if (offset !== null) {
                    if (target.id === 'details'){
                        offset -= 48;
                    }
                    document.documentElement.scrollTo({
                        top: offset,
                        behavior: 'smooth'
                    });
                }
            }
        });
    });
    document.querySelectorAll('.agenda').forEach((agendaContainer) => {
        const agenda = document.createElement('div');
        agenda.classList.add('agenda');
        agendaContainer.appendChild(agenda);

        const url = agendaContainer.dataset.url;
        /* L'URL du proxy est figée au build sur « localhost » : env_service_url la
         * remet sur l'hôte qui sert la page (mobile branché sur le réseau local). */
        const proxyBase = env_service_url(agendaContainer.dataset.proxy ?? defaultProxyCacheBase());
        overlapMsg = agendaContainer.dataset.overlapMsg ?? overlapMsg;

        if (url !== undefined) {

            const calendar = new Calendar(agenda, {
                initialView: 'dayGridMonth',
                locale: 'fr',
                plugins: [iCalendarPlugin, multiMonthPlugin, dayGridPlugin, interactionPlugin],
                headerToolbar: {
                    start: 'today',
                    center: 'title',
                    end: 'prev,next'
                },
                selectable: true,
                selectLongPressDelay: 250,
                eventLongPressDelay: 250,
                longPressDelay: 250,
                buttonText: {
                    today: 'Aujourd\'hui',
                    month: 'Mois',
                    week: 'Semaine',
                    day: 'Jour',
                    list: 'Liste'
                },
                // On small screens, let FullCalendar size itself to content to avoid a large empty block.
                contentHeight: window.innerWidth < 1024
                    ? 'auto'
                    : Math.max(Math.min(window.innerHeight - 250, 500), 400),
                fixedWeekCount: false,
                // Pas de borne basse : les dates passées restent affichées avec leur disponibilité.
                // Elles sont simplement rendues non sélectionnables via selectAllow.
                validRange: {
                    end: new Date(new Date().setFullYear(new Date().getFullYear() + 3))
                },
                selectAllow: function (info) {
                    return localDateStr(info.start) >= localDateStr(new Date());
                },
                events: {
                    url: `${proxyBase}?url=${encodeURIComponent(url)}`,
                    format: 'ics',
                    success: function (data) {
                        // iCal convention:
                        // - All-day (no 'T'): DTEND is EXCLUSIVE → checkoutStr = DTEND-1
                        // - Timed (has 'T'):  DTEND is the actual checkout datetime → checkoutStr = DTEND date
                        //
                        // We return one single-day FC event per day so each cell gets its own
                        // fc-bg-event div. Multi-day events use a harness spanning N cells and
                        // break per-cell gradient CSS (the gradient applies to the full harness width).
                        bookedRanges = [];
                        const singleDayEvents = [];
                        data.forEach((event) => {
                            if (!event.start || !event.end) return;
                            const startStr    = event.start.substring(0, 10);
                            const endDateStr  = event.end.substring(0, 10);
                            const isAllDay    = !event.start.includes('T');
                            const checkoutStr = isAllDay ? subtractOneDay(endDateStr) : endDateStr;

                            bookedRanges.push({ checkinStr: startStr, checkoutStr });

                            let cursor = startStr;
                            while (cursor <= checkoutStr) {
                                const isCheckin  = cursor === startStr;
                                const isCheckout = cursor === checkoutStr;
                                singleDayEvents.push({
                                    start:           cursor,
                                    end:             addOneDay(cursor),
                                    title:           '',
                                    display:         'background',
                                    backgroundColor: 'rgba(251, 191, 36, 0.5)',
                                    classNames:      isCheckin && !isCheckout ? ['bg-booked', 'bg-booked-checkin']
                                                   : isCheckout && !isCheckin  ? ['bg-booked', 'bg-booked-checkout']
                                                   :                             ['bg-booked'],
                                });
                                cursor = addOneDay(cursor);
                            }
                        });
                        return singleDayEvents;
                    }
                },

                datesSet: function () {
                    updateSelectionDisplay();
                },

                select: function (info) {
                    checkin.value = info.startStr;
                    // info.endStr is exclusive in FullCalendar — subtract 1 day to get the real checkout date
                    const coD = new Date(info.endStr + 'T12:00:00');
                    coD.setDate(coD.getDate() - 1);
                    checkout.value = localDateStr(coD);
                    checkout.min = checkin.value;
                    checkin.max = checkout.value;
                    calculatePrice();
                    /* validateStay() remet à zéro le marquage d'erreur des dates :
                     * on l'interroge avant updateSelectionDisplay(), à qui revient le dernier mot. */
                    const stayIsBookable = validateStay() === null;
                    updateSelectionDisplay();
                    /* Sur petit écran le formulaire est empilé sous le calendrier :
                     * dès que la plage choisie est réservable, on l'amène à l'écran. */
                    if (stayIsBookable && ui_is_small_screen()) {
                        scrollToBookingForm();
                    }
                }

            });
            calendar.render();
            calendarInstance = calendar;

        }
    });

    function updateSelectionDisplay() {
        if (!calendarInstance) return;
        // Use querySelector to avoid TDZ issues when called from datesSet during initial render
        const checkinEl = document.querySelector('.booking-form #checkin');
        const checkoutEl = document.querySelector('.booking-form #checkout');
        if (!checkinEl || !checkoutEl) return;

        const eventsToRemove = calendarInstance.getEvents().filter(
            ev => ev.groupId === '__selection' || ev.groupId === '__overlap'
        );
        eventsToRemove.forEach(ev => ev.remove());
        calendarInstance.unselect();

        const startStr = checkinEl.value;
        const endStr = checkoutEl.value;

        if (!startStr || !endStr || startStr >= endStr) {
            checkinEl.classList.remove('error');
            checkoutEl.classList.remove('error');
            return;
        }

        calendarInstance.addEvent({
            groupId: '__selection',
            start: startStr,
            end: addOneDay(endStr),  // endStr is inclusive (checkout day) — add 1 for FC exclusive end
            display: 'background',
            classNames: ['bg-selection']
        });

        const newStartMs = dateHourMs(startStr, CHECKIN_HOUR);
        const newEndMs   = dateHourMs(endStr,   CHECKOUT_HOUR);
        let hasOverlap = false;

        bookedRanges.forEach(r => {
            const bookedStartMs = dateHourMs(r.checkinStr,  CHECKIN_HOUR);
            const bookedEndMs   = dateHourMs(r.checkoutStr, CHECKOUT_HOUR);
            const oStartMs = Math.max(newStartMs, bookedStartMs);
            const oEndMs   = Math.min(newEndMs,   bookedEndMs);
            if (oStartMs < oEndMs) {
                hasOverlap = true;
                const oStartDay = localDateStr(new Date(oStartMs));
                const oEndDay   = addOneDay(localDateStr(new Date(oEndMs)));
                calendarInstance.addEvent({
                    groupId: '__overlap',
                    start: oStartDay,
                    end:   oEndDay,
                    display: 'background',
                    classNames: ['bg-overlap']
                });
            }
        });

        if (hasOverlap) {
            checkinEl.classList.add('error');
            checkoutEl.classList.add('error');
            ui_shake(calendarInstance.el);
            if (typeof Snackbar !== 'undefined') {
                Snackbar.show({ text: overlapMsg, showAction: false, duration: 4000, pos: 'bottom-center', backgroundColor: '#dc2626', color: 'white' });
            }
        } else {
            checkinEl.classList.remove('error');
            checkoutEl.classList.remove('error');
        }
    }


    /* Déclaration de fonction (et non const) : appelée depuis le calendrier, construit plus haut. */
    function scrollToBookingForm() {
        ui_scroll_to(document.querySelector('.booking-form .bl.c'));
    }

    const form = document.querySelector('.booking-form');

    const checkin = form.querySelector('#checkin');
    const checkout = form.querySelector('#checkout');
    const nights = form.querySelector('#nights');
    const adults = form.querySelector('#adults');
    const children = form.querySelector('#children');
    const babies = form.querySelector('#babies');
    const travelersContainer = form.querySelector('.travelers');
    const travelersSummary = form.querySelector('.travelers-summary');
    const taxAmount = form.querySelector('.tax-amount');
    const cleaningCheckbox = form.querySelector('#cleaning');
    const total = form.querySelector('#total');

    /* Forfait ménage obligatoire : la case reste cochée, elle n'est là que pour information */
    const cleaningMsg = form.dataset.cleaningMsg ?? '';
    const refuseCleaningUncheck = (e) => {
        e.preventDefault();
        if (typeof Snackbar !== 'undefined' && cleaningMsg) {
            Snackbar.show({ text: cleaningMsg, showAction: false, duration: 3000, pos: 'bottom-center', backgroundColor: '#dc2626', color: 'white' });
        }
    };
    cleaningCheckbox?.addEventListener('click', refuseCleaningUncheck);
    cleaningCheckbox?.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') refuseCleaningUncheck(e);
    });

    const capacity = Number(travelersContainer?.dataset.capacity) || 0;

    const prices = JSON.parse(form.dataset.price);
    const minNights = resolveBaseNights(prices);
    const minNightsError = form.querySelector('.min-nights-error');
    const minNightsMsg = form.dataset.minNightsMsg ?? '';

    /* Un séjour plus court que le forfait minimum n'est pas réservable */
    function checkMinNights(howManyNights) {
        const tooShort = howManyNights > 0 && howManyNights < minNights;
        if (minNightsError) {
            minNightsError.textContent = tooShort ? minNightsMsg : '';
            minNightsError.hidden = !tooShort;
        }
        checkin.classList.toggle('error', tooShort);
        checkout.classList.toggle('error', tooShort);
        return !tooShort;
    }


    const MS_PER_DAY = 1000 * 60 * 60 * 24;

    /* Les montants sont toujours affichés avec deux décimales */
    function formatAmount(value) {
        return Number(value).toLocaleString(document.documentElement.lang || 'fr', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    function syncCheckoutFromNights() {
        if (!checkin.value) return;
        const howManyNights = Math.max(1, Number(nights.value) || 0);
        const checkinDate = new Date(checkin.value);
        if (Number.isNaN(checkinDate.getTime())) return;
        const checkoutDate = new Date(checkinDate.getTime() + howManyNights * MS_PER_DAY);
        checkout.value = checkoutDate.toISOString().split('T')[0];
    }

    function calculatePrice() {

        let howManyNights = Number(nights.value) || 0;
        let checkinDate = null;

        if (checkin.value !== '') {
            const parsed = new Date(checkin.value);
            if (!Number.isNaN(parsed.getTime())) checkinDate = parsed;
        }

        if (checkin.value !== '' && checkout.value !== '') {
            const checkoutDate = new Date(checkout.value);
            const computedNights = checkinDate ? Math.round((checkoutDate - checkinDate) / MS_PER_DAY) : NaN;

            if (Number.isFinite(computedNights) && computedNights > 0) {
                howManyNights = computedNights;
                nights.value = howManyNights;
            }
        }


        checkMinNights(howManyNights);

        const location = computeStayPrice(prices, howManyNights);
        const cleaning = Number(prices.cleaning) || 0;
        const taxRatio = Number(prices.taxRatio) || 0;
        /* Taxe de séjour arrondie au centime */
        const tax = Math.round(location * taxRatio * 100) / 100;
        const globalPrice = Math.round((location + cleaning + tax) * 100) / 100;

        if (taxAmount) taxAmount.textContent = `${formatAmount(Number.isFinite(tax) ? tax : 0)} €`;

        total.value = Number.isFinite(globalPrice) ? formatAmount(globalPrice) : '';

        ui_shake(total);
    }

    calculatePrice();


    form.querySelectorAll('input, select, textarea').forEach(item => {
        item.addEventListener('invalid', function () {
            this.classList.add('error');
        });
    });

    /* Validation à la volée de l'email et du téléphone */
    const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
    const PHONE_PATTERN = /^\+?\d[\d\s.\-()]{7,}$/;

    /* pattern à null : seule la présence d'une valeur est contrôlée */
    function watchField(input, pattern) {
        if (!input) return;
        const field = input.closest('.field');
        let touched = false;
        const validate = () => {
            if (!touched) return;
            const value = input.value.trim();
            const invalid = value === ''
                ? Boolean(input.required) && pattern === null
                : Boolean(pattern) && !pattern.test(value);
            input.classList.toggle('error', invalid);
            field?.classList.toggle('is-invalid', invalid);
        };
        input.addEventListener('blur', () => { touched = true; validate(); });
        input.addEventListener('input', validate);
    }

    watchField(form.querySelector('#name'), null);
    watchField(form.querySelector('#email'), EMAIL_PATTERN);
    watchField(form.querySelector('#phone'), PHONE_PATTERN);

    checkin.min = new Date().toISOString().split('T')[0];
    checkin.addEventListener('change', function () {
        if (checkin.checkValidity()) {
            checkout.min = checkin.value;
            if (calendarInstance && checkin.value) calendarInstance.gotoDate(checkin.value);
        } else {
            checkin.classList.add('error');
        }
        calculatePrice();
        updateSelectionDisplay();
    });

    checkout.addEventListener('change', function () {
        if (checkout.checkValidity()) {
            checkin.max = checkout.value;
        } else {
            checkout.classList.add('error');
        }
        calculatePrice();
        updateSelectionDisplay();
    });

    const babyGear = form.querySelector('.baby-gear');
    const babyGearCheckbox = form.querySelector('#baby-gear');

    function updateTravelersSummary() {
        if (!travelersSummary) return;
        const a = Number(adults?.value) || 0;
        const c = Number(children?.value) || 0;
        const b = Number(babies?.value) || 0;
        travelersSummary.textContent = String(a + c + b);
    }

    function updateBabyGearVisibility() {
        if (!babyGear) return;
        const count = Number(babies?.value) || 0;
        if (count > 0) {
            babyGear.hidden = false;
        } else {
            babyGear.hidden = true;
            if (babyGearCheckbox) babyGearCheckbox.checked = false;
        }
    }

    function clampTravelers() {
        if (!adults || !children) return;
        let a = Number(adults.value) || 0;
        let c = Number(children.value) || 0;
        let b = Number(babies?.value) || 0;
        if (capacity > 0) {
            let travelersCount = a + c + b;
            if (travelersCount > capacity) {
                /* Auto-decrement adults first (down to 1) to absorb the overflow */
                const reducible = Math.max(0, a - 1);
                const take = Math.min(reducible, travelersCount - capacity);
                a -= take;
                travelersCount -= take;
                /* If still over capacity, clamp children, then babies */
                if (travelersCount > capacity) {
                    const overflow = travelersCount - capacity;
                    const childTake = Math.min(c, overflow);
                    c -= childTake;
                    travelersCount -= childTake;
                    if (travelersCount > capacity) b = Math.max(0, b - (travelersCount - capacity));
                }
                adults.value = String(a);
                children.value = String(c);
                if (babies) babies.value = String(b);
            }
        }
    }

    function enforceCapacityLimits() {
        if (!adults || !children) return;
        const a = Number(adults.value) || 0;
        const c = Number(children.value) || 0;
        const b = Number(babies?.value) || 0;
        const travelersTotal = a + c + b;
        const atCapacity = capacity > 0 && travelersTotal >= capacity;
        const canFreeSeat = a > 1; /* one slot can always be reclaimed from adults */
        const adultsCounter = adults.closest('.counter');
        const childrenCounter = children.closest('.counter');
        const babiesCounter = babies?.closest('.counter');
        const adultsUp = adultsCounter?.querySelector('.step-up');
        const childrenUp = childrenCounter?.querySelector('.step-up');
        const babiesUp = babiesCounter?.querySelector('.step-up');
        const adultsDown = adultsCounter?.querySelector('.step-down');
        /* Adults: can't grow past capacity */
        if (adultsUp) adultsUp.disabled = atCapacity || a >= (Number(adults.max) || Infinity);
        /* Adults: never below 1 */
        if (adultsDown) adultsDown.disabled = a <= 1;
        /* Children/babies: blocked only when full AND no adult to give up */
        if (childrenUp) childrenUp.disabled = c >= (Number(children.max) || Infinity) || (atCapacity && !canFreeSeat);
        if (babiesUp) babiesUp.disabled = b >= (Number(babies.max) || Infinity) || (atCapacity && !canFreeSeat);
    }

    const onTravelersChange = () => {
        clampTravelers();
        updateTravelersSummary();
        updateBabyGearVisibility();
        enforceCapacityLimits();
        calculatePrice();
    };

    [adults, children, babies].forEach((el) => {
        el?.addEventListener('change', onTravelersChange);
        el?.addEventListener('input', onTravelersChange);
    });
    babyGearCheckbox?.addEventListener('change', calculatePrice);

    onTravelersChange();

    nights.addEventListener('change', () => {
        if ((Number(nights.value) || 0) < minNights) nights.value = String(minNights);
        syncCheckoutFromNights();
        calculatePrice();
        if (calendarInstance && checkin.value) calendarInstance.gotoDate(checkin.value);
        updateSelectionDisplay();
    });

    /* ---- Envoi de la demande de réservation (application Gotan « contact-form ») ---- */

    const mailLabels = JSON.parse(form.dataset.mailLabels ?? '{}');
    const pageLang = document.documentElement.lang || 'fr';
    /* Les intitulés de conditions se terminent par « : » et portent parfois une précision
     * entre parenthèses : inutile dans un nom de champ ou une ligne de récapitulatif. */
    const cleanLabel = (label) => String(label ?? '')
        .replace(/\s*:\s*$/, '')
        .replace(/\s*\([^)]*\)\s*$/, '');
    const formatDate = (value) => {
        if (!value) return '';
        const parsed = new Date(`${value}T12:00:00`);
        return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString(pageLang);
    };

    function showFormError(text) {
        if (typeof Snackbar === 'undefined' || !text) return;
        Snackbar.show({ text, showAction: false, duration: 5000, pos: 'bottom-center', backgroundColor: '#dc2626', color: 'white' });
    }

    /* Message neutre : une consigne à suivre, pas une erreur (couleur du formulaire) */
    function showFormInfo(text) {
        if (typeof Snackbar === 'undefined' || !text) return;
        Snackbar.show({ text, showAction: false, duration: 6000, pos: 'bottom-center', backgroundColor: '#283618', color: 'white' });
    }

    /** @return {string|null} message d'erreur bloquant, ou null si le séjour est valide */
    function validateStay() {
        if (!checkin.value || !checkout.value) return null;

        const stayNights = Math.round(
            (new Date(checkout.value) - new Date(checkin.value)) / MS_PER_DAY
        );
        if (!checkMinNights(stayNights) || stayNights < minNights) return minNightsMsg;

        const newStartMs = dateHourMs(checkin.value, CHECKIN_HOUR);
        const newEndMs   = dateHourMs(checkout.value, CHECKOUT_HOUR);
        const hasOverlap = bookedRanges.some(r =>
            newStartMs < dateHourMs(r.checkoutStr, CHECKOUT_HOUR) &&
            newEndMs   > dateHourMs(r.checkinStr,  CHECKIN_HOUR)
        );
        return hasOverlap ? overlapMsg : null;
    }

    /** Récapitulatif du séjour : sert au corps du mail et à l'écran de confirmation.
     * @return {Array<[string, string]>} paires intitulé / valeur */
    function buildStayRecap() {
        const entries = [];
        const add = (label, value) => {
            const text = String(value ?? '').trim();
            if (label && text !== '') entries.push([cleanLabel(label), text]);
        };

        add(mailLabels.gite, form.dataset.gite);
        add(mailLabels.from, formatDate(checkin.value));
        add(mailLabels.to, formatDate(checkout.value));
        add(mailLabels.nights, nights.value);
        add(mailLabels.adults, adults?.value);
        add(mailLabels.children, children?.value);
        add(mailLabels.babies, babies?.value);
        if (Number(babies?.value) > 0) {
            add(mailLabels.babyGear, babyGearCheckbox?.checked ? mailLabels.yes : mailLabels.no);
        }
        if (cleaningCheckbox) add(mailLabels.cleaning, `${Number(prices.cleaning) || 0} €`);
        if (taxAmount) add(mailLabels.tax, taxAmount.textContent);
        add(mailLabels.total, `${total.value} €`);
        add(mailLabels.address, form.querySelector('#address')?.value);
        add(mailLabels.language, pageLang.toUpperCase());

        return entries;
    }

    /** Description de l'événement d'agenda : le corps du mail, en texte simple.
     * Le gîte ouvre la description et les dates sont celles de l'événement :
     * les lignes correspondantes du récapitulatif n'y apprendraient rien.
     * @return {string} gîte, coordonnées, message éventuel, puis le récapitulatif */
    function buildCalendarDescription(recap, contact, message) {
        const alreadyKnown = [mailLabels.gite, mailLabels.from, mailLabels.to].map(cleanLabel);
        const lines = [form.dataset.gite, ''];
        if (mailLabels.identityTitle) lines.push(mailLabels.identityTitle);
        lines.push(...[contact.name, contact.email, contact.phone].filter((value) => value !== ''));
        if (message !== '') lines.push('', message);
        lines.push('');
        recap.forEach(([label, value]) => {
            if (!alreadyKnown.includes(label)) lines.push(`${label} : ${value}`);
        });
        return lines.join('\n');
    }

    /** Instant absolu (ISO, UTC) d'une heure locale du gîte.
     * Outlook n'a pas d'équivalent au paramètre « ctz » de Google : il faut lui donner
     * l'instant, pas l'heure au mur. Le décalage est relevé à la date visée, heure d'été
     * comprise — les bornes du séjour (16 h, 10 h) ne tombent jamais dans l'heure d'un
     * changement d'heure, une seule passe suffit donc.
     * @return {string} « 2026-08-25T14:00:00Z » pour une arrivée à 16 h fin août */
    function giteHourToISO(dateStr, hour) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const wallClockAsUTC = Date.UTC(y, m - 1, d, hour);
        const shown = new Intl.DateTimeFormat('en-US', {
            timeZone: CALENDAR_TIMEZONE, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        }).formatToParts(new Date(wallClockAsUTC)).reduce((parts, part) => {
            parts[part.type] = Number(part.value);
            return parts;
        }, {});
        /* hour % 24 : à minuit, « en-US » en 24 h affiche 24 et non 0. */
        const offsetMs = Date.UTC(shown.year, shown.month - 1, shown.day, shown.hour % 24, shown.minute)
            - wallClockAsUTC;
        return new Date(wallClockAsUTC - offsetMs).toISOString().replace(/\.\d{3}Z$/, 'Z');
    }

    /** Lien « Ajouter à Google Agenda » : événement aux bornes du séjour, de l'arrivée
     * (16 h) au départ (10 h) — les heures qui servent déjà au contrôle de chevauchement
     * des réservations.
     * @return {string} chaîne vide tant que les dates du séjour manquent */
    function buildCalendarLink(title, description) {
        if (!checkin.value || !checkout.value) return '';
        const stamp = (dateStr, hour) => `${dateStr.replaceAll('-', '')}T${String(hour).padStart(2, '0')}0000`;
        const params = new URLSearchParams({
            action: 'TEMPLATE',
            text: title,
            dates: `${stamp(checkin.value, CHECKIN_HOUR)}/${stamp(checkout.value, CHECKOUT_HOUR)}`,
            ctz: CALENDAR_TIMEZONE,
            details: description
        });
        if (form.dataset.location) params.set('location', form.dataset.location);
        return `https://calendar.google.com/calendar/render?${params}`;
    }

    /** Lien « Ajouter à Outlook » : même événement, dans l'agenda Outlook.com.
     * Le point d'entrée est « /owa/ » : c'est lui qu'Outlook réécrit aujourd'hui vers son
     * gestionnaire de deeplink. La forme « /calendar/0/deeplink/compose », très répandue,
     * n'est plus routée et retombe sur la page produit de microsoft.com.
     * Un compte professionnel Microsoft 365 passerait par outlook.office.com — les deux
     * hôtes ne se répondent pas, il faut en choisir un.
     * @return {string} chaîne vide tant que les dates du séjour manquent */
    function buildOutlookLink(title, description) {
        if (!checkin.value || !checkout.value) return '';
        const params = new URLSearchParams({
            path: '/calendar/action/compose',
            rru: 'addevent',
            subject: title,
            startdt: giteHourToISO(checkin.value, CHECKIN_HOUR),
            enddt: giteHourToISO(checkout.value, CHECKOUT_HOUR),
            body: description
        });
        if (form.dataset.location) params.set('location', form.dataset.location);
        return `https://outlook.live.com/owa/?${params}`;
    }

    /** Échappement iCalendar (RFC 5545) : sans lui, une virgule dans le récapitulatif
     * couperait la valeur en deux et le fichier deviendrait illisible. */
    const escapeICS = (value) => String(value ?? '')
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\n/g, '\\n');

    /** Repliage des lignes à 75 octets (RFC 5545), sans jamais couper un caractère
     * UTF-8 en deux : la suite est préfixée d'une espace, qui compte dans la limite. */
    function foldICSLine(line) {
        const encoder = new TextEncoder();
        const chunks = [];
        let current = '';
        let bytes = 0;
        for (const char of line) {
            const size = encoder.encode(char).length;
            if (bytes + size > (chunks.length === 0 ? 75 : 74)) {
                chunks.push(current);
                current = '';
                bytes = 0;
            }
            current += char;
            bytes += size;
        }
        chunks.push(current);
        return chunks.join('\r\n ');
    }

    /** Calendrier du séjour, joint au mail : la voie qui marche partout — Apple Calendrier,
     * Thunderbird, Outlook de bureau — là où les deux boutons supposent un agenda en ligne.
     * @return {string} chaîne vide tant que les dates du séjour manquent */
    function buildICS(title, description) {
        if (!checkin.value || !checkout.value) return '';
        const stamp = (iso) => iso.replace(/[-:]/g, '');
        const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        return [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Ferme de la Haute-Drève//Réservation//FR',
            'CALSCALE:GREGORIAN',
            'METHOD:PUBLISH',
            'BEGIN:VEVENT',
            `UID:${checkin.value}-${checkout.value}-${slug}@ferme-de-la-haute-dreve`,
            `DTSTAMP:${stamp(new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'))}`,
            `DTSTART:${stamp(giteHourToISO(checkin.value, CHECKIN_HOUR))}`,
            `DTEND:${stamp(giteHourToISO(checkout.value, CHECKOUT_HOUR))}`,
            `SUMMARY:${escapeICS(title)}`,
            `DESCRIPTION:${escapeICS(description)}`,
            form.dataset.location ? `LOCATION:${escapeICS(form.dataset.location)}` : '',
            'END:VEVENT',
            'END:VCALENDAR'
        ].filter(Boolean).map(foldICSLine).join('\r\n');
    }

    async function sendBookingRequest() {
        const recap = buildStayRecap();
        const contactName = form.querySelector('#name')?.value.trim() ?? '';
        const contactEmail = form.querySelector('#email')?.value.trim() ?? '';
        const contactPhone = form.querySelector('#phone')?.value.trim() ?? '';
        const needs = form.querySelector('#message')?.value.trim() ?? '';
        const message = needs !== '' ? needs : (form.dataset.defaultMessage ?? '-');

        const body = new URLSearchParams();
        body.set('form_id', __CONTACT_FORM_ID__);
        body.set('form_key', __CONTACT_FORM_KEY__);
        /* Le gabarit du mail suit la langue de la page, comme son contenu. */
        body.set('lang', pageLang);
        /* L'utilisateur reçoit une copie de sa demande */
        body.set('copy', 'true');
        body.set('name', contactName);
        body.set('email', contactEmail);
        body.set('tel', contactPhone);
        body.set('subject', [
            form.dataset.subject,
            form.dataset.gite,
            `${formatDate(checkin.value)} → ${formatDate(checkout.value)}`
        ].filter(Boolean).join(' — '));
        body.set('message', message);
        recap.forEach(([label, value]) => body.set(`field_${label}`, value));

        /* Boutons d'agenda dans le mail : l'événement s'intitule « <gîte> - <personne> »
         * et reprend le corps du mail en description. Le message par défaut n'y a pas
         * sa place : il ne dit rien que l'événement ne dise déjà.
         * Les pictos sont servis par le site — un mail ne peut pointer que sur une URL absolue. */
        const eventTitle = [form.dataset.gite, contactName].filter(Boolean).join(' - ');
        const eventDescription = buildCalendarDescription(
            recap, { name: contactName, email: contactEmail, phone: contactPhone }, needs
        );
        const addCalendarButton = (label, url, icon) => {
            if (!label || url === '') return;
            body.set(`action_${label}`, url);
            body.set(`icon_${label}`, `${location.origin}/${icon}`);
        };
        addCalendarButton(mailLabels.addToGoogleCalendar,
            buildCalendarLink(eventTitle, eventDescription), 'google-calendar.png');
        addCalendarButton(mailLabels.addToOutlook,
            buildOutlookLink(eventTitle, eventDescription), 'outlook.png');

        /* Troisième voie : le fichier .ics, joint au mail par contact-form. Son nom se lit
         * dans la boîte de réception sans l'ouvrir — d'où le gîte, la personne et la date
         * d'arrivée. La barre oblique des dates françaises n'a pas sa place dans un nom
         * de fichier : elle passe en tiret. */
        const ics = buildICS(eventTitle, eventDescription);
        if (ics !== '') {
            const icsFileName = [
                mailLabels.icsFileTitle,
                form.dataset.gite,
                contactName,
                formatDate(checkin.value).replaceAll('/', '-')
            ].filter(Boolean).join(' - ') + '.ics';
            body.set('ics', ics);
            body.set('ics_name', icsFileName);
            if (mailLabels.icsHint) body.set('ics_hint', mailLabels.icsHint.replace('{file}', icsFileName));
        }

        const contactFormURL = env_is_local()
            ? env_service_url(__CONTACT_FORM_URL_LOCAL__)
            : __CONTACT_FORM_URL_SERVER__;

        const response = await fetch(contactFormURL, { method: 'POST', body });
        if (!response.ok) throw new Error(`contact-form: HTTP ${response.status}`);
        return recap;
    }

    function fillConfirmationRecap(recap) {
        const list = form.querySelector('.confirmation-recap');
        if (!list) return;
        list.replaceChildren();
        recap.forEach(([label, value]) => {
            const dt = document.createElement('dt');
            dt.textContent = label;
            const dd = document.createElement('dd');
            dd.textContent = value;
            list.append(dt, dd);
        });
    }

    const ctaButton = form.querySelector('.total .submit button');
    const ctaLabel = ctaButton?.querySelector('.cta-label');
    const ctaDefaultLabel = ctaLabel?.textContent ?? '';

    function setSending(sending) {
        if (ctaButton) ctaButton.disabled = sending;
        if (ctaLabel) ctaLabel.textContent = sending ? (form.dataset.sendingLabel ?? ctaDefaultLabel) : ctaDefaultLabel;
        form.classList.toggle('is-sending', sending);
    }

    /* La demande part à la sortie de l'étape « coordonnées » ; l'étape suivante confirme l'envoi. */
    const contactStep = form.querySelector('#email')?.closest('.step');
    let requestSent = false;

    form.addEventListener('step:leave', function (event) {
        const { step, resume } = event.detail;
        if (step !== contactStep || requestSent) return;

        event.preventDefault();

        const stayError = validateStay();
        if (stayError) {
            showFormError(stayError);
            return;
        }

        setSending(true);
        sendBookingRequest()
            .then((recap) => {
                requestSent = true;
                fillConfirmationRecap(recap);
                resume();
            })
            .catch((error) => {
                console.error(error);
                showFormError(form.dataset.sendError);
            })
            .finally(() => setSending(false));
    });

    /* Le formulaire n'est jamais posté nativement : tout passe par contact-form. */
    form.addEventListener('submit', (event) => event.preventDefault());

    /* ---- Adresse renseignée par géolocalisation (géocodage inverse Nominatim) ---- */

    const proxyCacheBase = env_service_url(form.dataset.proxy ?? defaultProxyCacheBase());

    /* Pays où le numéro précède la voie ; ailleurs il la suit (« Hauptstraße 12 »). */
    const HOUSE_NUMBER_FIRST = ['be', 'fr', 'lu', 'gb', 'ie', 'us', 'ca', 'au', 'nz'];

    /** Met en forme la réponse Nominatim en adresse postale de trois lignes.
     * @param address {object|undefined} bloc « address » de la réponse
     * @return {string} chaîne vide si la position ne correspond à aucune voie */
    function formatGeolocatedAddress(address) {
        if (!address) return '';
        const number = address.house_number ?? '';
        const road = address.road ?? address.pedestrian ?? address.footway ?? '';
        if (road === '') return '';
        const city = address.city ?? address.town ?? address.village
            ?? address.municipality ?? address.hamlet ?? '';
        const street = HOUSE_NUMBER_FIRST.includes(address.country_code)
            ? `${number} ${road}`.trim()
            : `${road} ${number}`.trim();
        return [street, [address.postcode, city].filter(Boolean).join(' '), address.country]
            .filter((line) => line !== undefined && line.trim() !== '')
            .join('\n');
    }

    const addressField = form.querySelector('#address');
    const geolocateButton = form.querySelector('.geolocate');

    if (addressField && geolocateButton) {
        const geolocateIdleLabel = geolocateButton.dataset.label ?? '';

        /* Un contexte non sécurisé (http ailleurs que sur le poste de dev) expose bien
         * navigator.geolocation mais toute demande y est refusée d'office. */
        const geolocationAvailable = 'geolocation' in navigator && window.isSecureContext !== false;

        /* Sans géolocalisation exploitable il n'y a rien à proposer : le bouton disparaît
         * et le champ adresse récupère toute la largeur. */
        const setGeolocateVisible = (visible) => {
            geolocateButton.hidden = !visible;
            geolocateButton.closest('.field-address')?.classList.toggle('no-geolocate', !visible);
        };

        /** État de l'autorisation avant de la demander, quand le navigateur sait le dire.
         * Safari < 16 et quelques navigateurs n'exposent pas l'API Permissions : « unknown »
         * laisse alors le navigateur arbitrer comme d'habitude.
         * @return {Promise<'granted'|'prompt'|'denied'|'unknown'>} */
        const permissionState = async () => {
            if (!navigator.permissions?.query) return 'unknown';
            try {
                return (await navigator.permissions.query({name: 'geolocation'})).state;
            } catch {
                return 'unknown';
            }
        };

        /* Autorisation bloquée pour le site : le navigateur ne la redemandera pas, le bouton
         * n'a plus d'utilité. Il réapparaît si l'autorisation est rétablie dans les réglages. */
        const syncGeolocateVisibility = async () => {
            setGeolocateVisible(geolocationAvailable && (await permissionState()) !== 'denied');
        };

        /* Cas connu d'avance : masquage immédiat, sans afficher un bouton qu'on escamote. */
        if (!geolocationAvailable) {
            setGeolocateVisible(false);
        } else {
            syncGeolocateVisibility();
            if (navigator.permissions?.query) {
                navigator.permissions.query({name: 'geolocation'})
                    .then((status) => { status.onchange = syncGeolocateVisibility; })
                    .catch(() => { /* état non observable : le bouton reste tel quel */ });
            }
        }

        /* Le bouton n'a pas de libellé visible : l'état passe par l'icône et l'infobulle. */
        const setLocating = (locating) => {
            geolocateButton.disabled = locating;
            geolocateButton.classList.toggle('is-locating', locating);
            const label = locating
                ? (geolocateButton.dataset.locating ?? geolocateIdleLabel)
                : geolocateIdleLabel;
            geolocateButton.setAttribute('aria-label', label);
            geolocateButton.title = label;
        };

        const currentPosition = (options) => new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, options);
        });

        /** Position de l'appareil, précision GPS d'abord.
         * macOS (CoreLocation) et les postes fixes échouent souvent en haute précision
         * (kCLErrorLocationUnknown) : la localisation réseau suffit pour une adresse postale.
         * @return {Promise<GeolocationPosition>} */
        const locate = async () => {
            try {
                return await currentPosition({enableHighAccuracy: true, timeout: 10000, maximumAge: 60000});
            } catch (error) {
                /* Un refus explicite ne se rejoue pas : la seconde tentative échouerait pareil. */
                if (error?.code === 1) throw error;
                return await currentPosition({enableHighAccuracy: false, timeout: 15000, maximumAge: 300000});
            }
        };

        geolocateButton.addEventListener('click', async function () {
            if (!geolocationAvailable) {
                showFormError(geolocateButton.dataset.unsupported);
                return;
            }

            /* Autorisation déjà refusée pour le site : le navigateur ne redemandera rien,
             * inutile de lancer une recherche qui échouera en silence. */
            const permission = await permissionState();
            if (permission === 'denied') {
                showFormError(geolocateButton.dataset.blocked);
                return;
            }

            setLocating(true);
            try {
                /* La fenêtre d'autorisation du navigateur va s'ouvrir : on explique
                 * pourquoi le bouton tourne en attendant la réponse. */
                if (permission !== 'granted') showFormInfo(geolocateButton.dataset.prompt);

                const position = await locate();
                /* Le géocodage inverse passe par le cache Gotan : le navigateur n'appelle
                 * jamais Nominatim directement (quota d'usage et CORS). */
                const nominatimURL = 'https://nominatim.openstreetmap.org/reverse'
                    + `?format=jsonv2&zoom=18&accept-language=${pageLang}`
                    + `&lat=${position.coords.latitude}&lon=${position.coords.longitude}`;
                const response = await fetch(`${proxyCacheBase}?url=${encodeURIComponent(nominatimURL)}`);
                if (!response.ok) throw new Error(`nominatim: HTTP ${response.status}`);

                const address = formatGeolocatedAddress((await response.json()).address);
                if (address === '') throw new Error('nominatim: aucune adresse à cette position');

                addressField.value = address;
                addressField.dispatchEvent(new Event('input', { bubbles: true }));
                ui_shake(addressField);
            } catch (error) {
                console.error(error);
                /* code 1 = PERMISSION_DENIED : refus de l'utilisateur, pas une panne */
                showFormError(error?.code === 1
                    ? geolocateButton.dataset.denied
                    : geolocateButton.dataset.error);
            } finally {
                setLocating(false);
            }
        });
    }

    form.querySelectorAll('.counter').forEach((counter) => {
        const input = counter.querySelector('input[type="number"]');
        if (!input) return;
        const stepDown = counter.querySelector('.step-down');
        const stepUp = counter.querySelector('.step-up');
        const parsedMin = Number(input.min);
        const parsedMax = Number(input.max);
        const min = input.min !== '' && Number.isFinite(parsedMin) ? parsedMin : -Infinity;
        const max = input.max !== '' && Number.isFinite(parsedMax) ? parsedMax : Infinity;

        /* Les compteurs voyageurs ont leurs propres règles (capacité du gîte) : elles priment sur min/max */
        const isTravelerCounter = [adults, children, babies].includes(input);

        const updateButtonsState = () => {
            const current = Number(input.value) || 0;
            if (stepDown) stepDown.disabled = current <= min;
            if (stepUp) stepUp.disabled = current >= max;
            if (isTravelerCounter) enforceCapacityLimits();
        };

        const change = (delta) => {
            const current = Number(input.value) || 0;
            const next = Math.min(max, Math.max(min, current + delta));
            if (next === current) return;
            input.value = next;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            updateButtonsState();
        };

        stepDown?.addEventListener('click', (e) => { e.preventDefault(); change(-1); });
        stepUp?.addEventListener('click', (e) => { e.preventDefault(); change(1); });
        input.addEventListener('input', updateButtonsState);
        updateButtonsState();
    });

    calculatePrice();
})
;


